import React, { useEffect, useMemo } from 'react';
import { Accordian } from '@shared/animations/AnimationVariants';
import { OfficeEntity } from "@features/desks/services/AdvanceHotDeskingService";
import { FloorAndAreaButton } from "./FloorAndAreaButton";
import moment from "moment";
import { GenericDropDownList } from '@shared/ui/molecules/GenericDropDownList';
import styled from "styled-components";
import { findOfficeInOffices, getOfficeChildren, getRootOffice, sortOffice } from "@shared/utils/OfficeHelper";
import {
  clearCarParkingPlans,
  clearSelectedArea,
  loadCarParkingPlansForActiveOffice,
  MapView,
  selectCarParkingPlans,
  selectedRootOffice,
  selectOffice,
  setSelectedMapView
} from "../../../store/officeView.duck";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveOffices,
  selectAllOfficeEntities,
  selectIsAdvanceHotDeskingLoading
} from "@features/desks/store/advanceHotDeskingSetup.duck";
import { selectActiveDay } from "../../../store/editMovements.duck";
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { iconPackPath } from "@shared/utils/WhereaboutsHelper";
import { FreeBusyStatus } from '@features/whereabouts/services/WhereaboutOptions';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { selectIconPack } from "@shared/store/auth.duck";
import { useTranslation } from "react-i18next";
import { OfficeCarParkPlanButton } from "./OfficeCarParkPlanButton";
import { setSelectedParkingPlanId } from "../ducks/carParkingMapView.duck";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function ChooseOfficeControls(props: Props) {
  const dispatch = useDispatch();
  const isOfficesLoading = useSelector(selectIsAdvanceHotDeskingLoading);
  const offices = useSelector(selectActiveOffices);
  const activeDay = useSelector(selectActiveDay);
  const activeOffice = useSelector(selectedRootOffice);
  const allOffices = useSelector(selectAllOfficeEntities);
  const iconPack = useSelector(selectIconPack);
  const carParkingPlans = useSelector(selectCarParkingPlans);
  const {t} = useTranslation();

  const activeParentOffice = useMemo(() =>
    (!!activeOffice?.parentId ? getRootOffice(allOffices, activeOffice) : activeOffice), [allOffices, activeOffice])

  const activeChildOffices = useMemo(() =>
    (allOffices
      .filter((o: OfficeEntity) => o.parentId === activeParentOffice?.id && o.active)
      .sort(sortOffice)), [allOffices, activeParentOffice]);

  const rootOfficeHasParkingPlan = activeParentOffice?.hasCarParkPlan;
  const rootOfficeId = activeParentOffice?.id;

  useEffect(() => {
    if (rootOfficeHasParkingPlan && rootOfficeId) {
      dispatch(loadCarParkingPlansForActiveOffice({rootOfficeId: rootOfficeId}));
    }
  }, [rootOfficeHasParkingPlan, dispatch, rootOfficeId]);

  const officesForDropdown = useMemo(() => offices.map((office: OfficeEntity) => ({...office, name: office.label})), [offices]);

  const onSelect = (officeId: any) => {
    const office = findOfficeInOffices(allOffices, parseInt(officeId));
    const officeChildren = getOfficeChildren(parseInt(officeId), allOffices);

    dispatch(setSelectedMapView(MapView.Office));
    dispatch(clearCarParkingPlans());
    dispatch(setSelectedParkingPlanId(0));

    if (officeChildren.length > 0) {
      dispatch(selectOffice(officeChildren[0]));
      dispatch(clearSelectedArea());
    } else if (office) {
      dispatch(selectOffice(office));
      dispatch(clearSelectedArea());
    } else {
      console.warn('No office found for', officeId);
    }
  }

  if (isOfficesLoading) {
    return null;
  } else if (offices.length === 0) {
    return (
      <Column>
        <OfficeImage style={{marginBottom: 24}} src={iconPackPath(iconPack, 'Office.svg', FreeBusyStatus.FREE)} alt="Ofifce" aria-hidden={false} />
        <HeadlineSmall style={{marginBottom: 8}}>{t('company-view-page.create-an-office.title')}</HeadlineSmall>
        <BodyRegular style={{marginBottom: 32}}>{t('company-view-page.create-an-office.description')}</BodyRegular>
        <OutlineButton size={ButtonSize.Large} click={() => window.location.href = '/manage/offices'} text="button.create-office" />
      </Column>)
  } else {
    return (
      <>
        <OfficeSelectDropDown selectedId={activeOffice?.id}
                              onSelect={(office: any) => onSelect(office)}
                              items={officesForDropdown}
                              placeholder="textinput.choose-an-office" />

        {activeChildOffices && activeChildOffices.length > 0 && <Accordian isOpen={!!activeParentOffice} style={{marginTop: 8}}>
          {activeChildOffices.map((floor: OfficeEntity, key: number) => (
            <FloorAndAreaButton key={key}
                                data-test={`floor-button-${floor.label}`}
                                office={floor}
                                onSelect={props.onSelect}
                                date={activeDay ?? moment()} />
          ))}
        </Accordian>}

        {carParkingPlans && carParkingPlans.length > 0 && <>
          {carParkingPlans.map((plan, key) => (
            <OfficeCarParkPlanButton key={key} plan={plan} date={activeDay} />))}
        </>}
      </>
    )
  }
}

interface Props {
  onSelect?: () => any;
}


const OfficeSelectDropDown = styled(GenericDropDownList)<any>`
  width: 100%;
`

const OfficeImage = styled.img`
  align-self: center;
  width: 96px;
`

